"use client"

import { useEffect, useMemo } from "react"
import Link from "@/components/ui/link"
import { usePathname, useSearchParams } from "@/lib/router"
import {
  Home, LayoutDashboard, Calendar, Clock, Settings, CreditCard, FolderOpen,
  MessageCircleWarning, ChartColumn, PanelLeftClose, PanelLeftOpen, type LucideIcon,
} from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { useNavigationTranslations, useComponentsTranslations } from "@/hooks/use-translations"
import { useLayoutStore } from "@/stores/layout-store"
import { useAreasStore } from "@/stores"
import type { Area } from "@/lib/convex-areas"

interface SidebarContentProps {
  idPrefix?: string
  /**
   * Icons-only rail. The mobile sheet always passes `false` — a drawer the
   * user deliberately opened should say what things are.
   */
  collapsed?: boolean
  /** The mobile sheet has its own dismiss, so it hides this. */
  showCollapse?: boolean
}

interface NavItem {
  name: string
  href: string
  icon: LucideIcon
}

export function SidebarContent({
  idPrefix: _idPrefix = "sidebar",
  collapsed = false,
  showCollapse = false,
}: SidebarContentProps) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const t = useNavigationTranslations()
  const tComponents = useComponentsTranslations()

  const areas = useAreasStore((state) => state.areas)
  const initializeAreas = useAreasStore((state) => state.initialize)
  useEffect(() => initializeAreas(), [initializeAreas])

  const currentAreaId = searchParams.get("area")

  // Blitztask is single-user, so there is no workspace to switch between and
  // the sidebar leads with navigation instead of an org picker.
  //
  // Grouped by what you came to do rather than listed flat: nine equal items
  // made Settings and Pricing look as important as Tasks. The account group is
  // pinned to the bottom, where nobody reads past it to reach their work.
  const groups: Array<{ label: string | null; items: NavItem[] }> = [
    {
      label: null,
      items: [
        { name: t('dashboard'), href: "/", icon: Home },
        { name: t('tasks'), href: "/tasks", icon: LayoutDashboard },
        { name: t('calendar'), href: "/calendar", icon: Calendar },
      ],
    },
    {
      label: t('group_focus'),
      items: [
        { name: t('timer'), href: "/timer", icon: Clock },
        { name: t('insights'), href: "/insights", icon: ChartColumn },
        { name: t('projects'), href: "/projects", icon: FolderOpen },
      ],
    },
  ]

  // The worlds themselves, under the navigation — the thing Notion's
  // Privat/Teamspaces split actually gets you, minus the tenancy. `scope` is a
  // label on an area, not a workspace: no members, no permissions, no seats.
  //
  // The split only appears once something is actually filed as work. Until
  // then a lone "Privat" heading over everything would be noise, so they list
  // under one neutral heading.
  const areaGroups = useMemo(() => {
    const visible = areas.filter((area) => !area.archived)
    if (visible.length === 0) return []

    const work = visible.filter((area) => area.scope === "work")
    if (work.length === 0) return [{ label: t("group_areas"), areas: visible }]

    return [
      { label: t("group_personal"), areas: visible.filter((area) => area.scope !== "work") },
      { label: t("group_work"), areas: work },
    ].filter((group) => group.areas.length > 0)
  }, [areas, t])

  const accountItems: NavItem[] = [
    { name: t('settings'), href: "/settings", icon: Settings },
    { name: t('pricing'), href: "/pricing", icon: CreditCard },
    { name: t('feedback'), href: "/feedback", icon: MessageCircleWarning },
  ]

  const renderItem = (item: NavItem) => {
    // An area link points at /tasks too, so plain path equality would light up
    // "Tasks" and the area at the same time. The area is the more specific
    // destination, so it wins.
    const active = pathname === item.href && !(item.href === "/tasks" && currentAreaId)

    const link = (
      <Link
        href={item.href}
        prefetch={false}
        aria-current={active ? "page" : undefined}
        className={cn(
          "relative flex h-9 items-center gap-3 rounded-md px-3 text-sm transition-colors",
          collapsed && "justify-center px-0",
          active
            ? "bg-accent font-medium text-accent-foreground"
            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
        )}
      >
        {/* A rail rather than a block of colour: it marks position without
            competing with the page for attention. */}
        {active && (
          <span
            aria-hidden
            className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary"
          />
        )}
        <item.icon className="h-4 w-4 flex-shrink-0" />
        {!collapsed && <span className="truncate">{item.name}</span>}
      </Link>
    )

    if (!collapsed) return <div key={item.name}>{link}</div>

    return (
      <Tooltip key={item.name} delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.name}</TooltipContent>
      </Tooltip>
    )
  }

  /**
   * An area, linking to its slice of the task list.
   *
   * The dot carries the area's own colour — the same colour it has on
   * `/insights` and in the capture review — so the sidebar teaches the
   * vocabulary the rest of the app uses. Collapsed, the dot is all there is,
   * which is why the colour has to be consistent.
   */
  const renderAreaItem = (area: Area) => {
    const href = `/tasks?area=${area.id}`
    const active = pathname === "/tasks" && currentAreaId === area.id

    const link = (
      <Link
        href={href}
        prefetch={false}
        aria-current={active ? "page" : undefined}
        className={cn(
          "relative flex h-8 items-center gap-3 rounded-md px-3 text-sm transition-colors",
          collapsed && "justify-center px-0",
          active
            ? "bg-accent font-medium text-accent-foreground"
            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
        )}
      >
        {active && (
          <span
            aria-hidden
            className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary"
          />
        )}
        {area.icon ? (
          <span aria-hidden className="text-xs leading-none">{area.icon}</span>
        ) : (
          <span
            aria-hidden
            className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
            style={{ backgroundColor: area.color }}
          />
        )}
        {!collapsed && <span className="truncate">{area.name}</span>}
      </Link>
    )

    if (!collapsed) return <div key={area.id}>{link}</div>

    return (
      <Tooltip key={area.id} delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{area.name}</TooltipContent>
      </Tooltip>
    )
  }

  const collapseButton = (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => useLayoutStore.getState().toggleSidebar()}
          aria-label={collapsed ? tComponents("sidebar_show") : tComponents("sidebar_hide")}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">
        {collapsed ? tComponents("sidebar_show") : tComponents("sidebar_hide")} · ⌘\
      </TooltipContent>
    </Tooltip>
  )

  return (
    <TooltipProvider>
      <div className="relative flex h-full flex-col">
        {/* Expanded, there is no button at all.

            The sidebar has no top row of its own — the logo lives in the app
            header — so a toggle here either claims a row of dead space or
            floats on top of the first nav item. Both were tried and both
            looked wrong. Collapsing is the seam (drag it left past the snap
            point) or ⌘\, which is how it was asked for in the first place. */}

        {/* In the rail it keeps its row: there is nothing else competing for
            the space, and with the sidebar collapsed it is the most likely
            thing to be reached for. */}
        {showCollapse && collapsed && (
          <div className="hidden justify-center px-2 pt-2 md:flex">{collapseButton}</div>
        )}

        <nav className={cn("flex-1 space-y-4 p-2", !collapsed && "md:p-3")}>
          {groups.map((group, index) => (
            <div key={group.label ?? index} className="space-y-0.5">
              {group.label && !collapsed && (
                <p className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  {group.label}
                </p>
              )}
              {group.label && collapsed && <div className="mx-2 mb-2 border-t" />}
              {group.items.map(renderItem)}
            </div>
          ))}

          {areaGroups.map((group) => (
            <div key={group.label} className="space-y-0.5">
              {collapsed ? (
                <div className="mx-2 mb-2 border-t" />
              ) : (
                <p className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                  {group.label}
                </p>
              )}
              {group.areas.map((area) => renderAreaItem(area))}
            </div>
          ))}
        </nav>

        <div className={cn("space-y-0.5 p-2", !collapsed && "md:p-3")}>
          <div className={cn("mb-2 border-t", collapsed && "mx-2")} />
          {accountItems.map(renderItem)}
        </div>

        {!collapsed && (
          <p className="select-none px-5 py-3 text-left text-xs text-muted-foreground">
            {t('ea_text')}
          </p>
        )}
      </div>
    </TooltipProvider>
  )
}
